// THE CLIENT SLACK LABELLED SET: its shapes, how an example reads, and the
// numbers.
//
// ⭐ PURE: no file, no database, no clock. tools/client-slack-eval.ts builds
// the set from a snapshot and does the reading and writing; this says what an
// example is, how a labeller sees it, whether a label is well formed, and what
// every metric means. Erasable TypeScript (bare node runs the tool).
//
// THE SET HOLDS REAL CLIENT WORDS and lives in eval/client-slack/, which git
// ignores. Nothing here carries any; a run's numbers (`run.json`) carry no
// string from the set (a test holds that).
//
// THE THREE KINDS OF EXAMPLE, one per question:
//
//   - a WAIT: one client message (the target) the wait rule opened a wait at.
//     Gold says which conversation it is in, which client messages are one
//     utterance with it, whether YWS owed a response, which YWS message (or
//     reaction) gave it and when, its kind, escalation and need line. C1–C4,
//     C7, C8 (and C5–C6, which a judge scores).
//   - a CONVERSATION: a thread or a run of top-level messages (the focus).
//     Gold lists the asks raised in it, each with owner, type, evidence,
//     status at the window's end and a brand-wide cluster. C9, C10.
//   - a TIMELINE: one ask followed across days and conversations. Gold says
//     whether it is real work, every message that is evidence of it, and its
//     status at 3–5 checkpoints. C11, C12.
//
// A MESSAGE'S ID is `m<k>`, k its place in its channel's time order over the
// whole snapshot, so one message has one id in every example and every batch.

export type Side = "yws" | "client" | "bot";
export type ExampleKind = "wait" | "conversation" | "timeline";

export interface EvalPerson {
  name: string;
  side: Side;
}

export interface EvalMessage {
  id: string;
  ts: string;
  /** A reply: its root's id. A root with replies: its own id. A lone message: null. */
  thread: string | null;
  replies: number;
  /** The Slack user id, for scoring against prod's rows; never shown to a labeller. */
  user: string | null;
  bot: boolean;
  text: string;
  files: string[];
  reactions: Array<{ name: string; users: string[] }>;
}

export interface EvalChannel {
  id: string;
  name: string;
  brand: string | null;
  people: Record<string, EvalPerson>;
  messages: EvalMessage[];
}

interface ExampleBase {
  id: string;
  kind: ExampleKind;
  channel: string;
  /** Inclusive index range into the channel's messages; `extra` adds replies of a window's thread that fall outside it. */
  window: { from: number; to: number };
  extra: number[];
  /** Why the builder picked it (never shown to a labeller). */
  strata: string[];
}

export interface WaitExample extends ExampleBase {
  kind: "wait";
  target: string;
  /** What today's rule said (provenance for E2; never shown to a labeller). */
  rule: { waitId: string; anchors: string[]; end: string | null; endedBy: "thread" | "mention" | null; answeredBy: string | null };
}

export interface ConversationExample extends ExampleBase {
  kind: "conversation";
  focus: string[];
}

export interface TimelineExample extends ExampleBase {
  kind: "timeline";
  seed: string;
  /** Which ask of the seed message is meant: prod's item title (the ask's name only, never its status). */
  hint: string;
  checkpoints: Array<{ id: string; ts: string }>;
  prod: { itemId: string };
}

export type Example = WaitExample | ConversationExample | TimelineExample;

// ---------------------------------------------------------------- labels

export const WHY_NEEDS = ["question", "request", "problem", "approval", "other_need"] as const;
export const WHY_NOT = ["ack", "small_talk", "answer_to_us", "fyi", "sign_off", "own_colleague", "retracted", "other"] as const;
export const RESOLUTIONS = ["answered", "acknowledged", "moved_off_slack", "superseded", "client_self_answered", "not_needed", "open"] as const;
export const ESCALATION_WHY = ["upset", "money", "churn", "repeated"] as const;
export const ASK_TYPES = ["task", "standing_instruction", "scheduling"] as const;
export const STATUSES = ["not_started", "in_progress", "waiting", "ongoing", "done", "dropped"] as const;
export const NEED_MAX = 140;

export type Status = (typeof STATUSES)[number];
export type AskType = (typeof ASK_TYPES)[number];
/** Who has the ball on an open ask (guide v4 §2.2); null on done, dropped and ongoing. */
export type Owes = "us" | "them" | null;
/** Guide v4's statuses: no `waiting` (waiting on the client is in progress, the client owing). */
export const V4_STATUSES = ["not_started", "in_progress", "ongoing", "done", "dropped"] as const;

export interface WaitLabel {
  id: string;
  segment: string[];
  turn: string[];
  needs_response: boolean;
  why: string;
  start: string | null;
  answered_by: string | null;
  resolution: string;
  kind: "ack" | "reply" | null;
  escalation: boolean;
  escalation_why: string | null;
  need: string | null;
  note: string;
  /** A candidate's (C5, C6); gold leaves them out. */
  emoji?: string[];
  quick?: string[];
}

export interface Ask {
  title: string;
  owner: "us" | "them";
  type: AskType;
  evidence: string[];
  status: Status;
  cluster: string;
  /** Guide v4 (gold v3): who has the ball at the window's end. Absent on v1–v3 labels and v3-era answers (`ballOf` reads them). */
  owes?: Owes;
}

export interface ConversationLabel {
  id: string;
  asks: Ask[];
  note: string;
}

export interface TimelineLabel {
  id: string;
  real: boolean;
  owner: "us" | "them" | null;
  type: AskType | null;
  evidence: string[];
  checkpoints: Record<string, Status>;
  /** Guide v4 (gold v3): who has the ball at each checkpoint. Absent on v1–v3 labels (`ballOf` reads them). */
  owes?: Record<string, Owes>;
  note: string;
  /** A candidate's: the messages at which it wrote to the item (C12). */
  updates?: string[];
}

export type Label = WaitLabel | ConversationLabel | TimelineLabel;

/** A reaction as an answer: `<message id>:<emoji name>`. */
export const REACTION = /^(m\d+):([a-z0-9_+\-']+)$/;

/**
 * An answer with any reaction's skin tone dropped (`m19:+1::skin-tone-4` →
 * `m19:+1`); anything else unchanged. Gold writes the emoji's name and a
 * model sometimes writes the tone too: the same answer, which the scorers
 * must not count as a miss (E9b), nor time as a message.
 */
export const stripTone = (answer: string | null | undefined): string | null => (answer === null || answer === undefined ? null : answer.replace(/::skin-tone-\d+$/, ""));

// ---------------------------------------------------------------- windows

/** The messages an example shows, in time order. */
export function windowMessages(ex: Example, ch: EvalChannel): EvalMessage[] {
  const out: EvalMessage[] = [];
  for (let i = ex.window.from; i <= ex.window.to && i < ch.messages.length; i++) out.push(ch.messages[i]);
  for (const i of ex.extra) if (i > ex.window.to || i < ex.window.from) out.push(ch.messages[i]);
  return out.sort((a, b) => idNum(a.id) - idNum(b.id));
}

export const idNum = (id: string): number => Number(id.slice(1));

// ---------------------------------------------------------------- rendering

const TZ = "America/New_York";
const when = new Intl.DateTimeFormat("en-US", { timeZone: TZ, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
export const TEXT_MAX = 1500;

/** A ts as a labeller reads it, New York time. */
export const stamp = (ts: string): string => when.format(new Date(Number(ts) * 1000));

const sideTag = (p: EvalPerson | undefined): string => (p === undefined ? "[client]" : p.side === "yws" ? "[YWS]" : p.side === "bot" ? "[bot]" : "[client]");

/** Slack's mrkdwn as words, every mention with the mentioned person's side. */
export function readable(text: string, people: Record<string, EvalPerson>): string {
  const out = text
    .replace(/<@(\w+)(?:\|[^>]*)?>/g, (_, u: string) => `@${people[u]?.name ?? "someone"} ${sideTag(people[u])}`)
    .replace(/<#\w+\|([^>]*)>/g, (_, name: string) => `#${name}`)
    .replace(/<!(here|channel|everyone)[^>]*>/g, (_, w: string) => `@${w}`)
    .replace(/<(https?:[^|>]+)\|([^>]+)>/g, (_, url: string, label: string) => `${label} (${url})`)
    .replace(/<(https?:[^>]+)>/g, (_, url: string) => url)
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/\s*\n\s*/g, " ⏎ ");
  return out.length > TEXT_MAX ? `${out.slice(0, TEXT_MAX)} …(cut)` : out;
}

/** One message as one line: id, time, author and side, thread, words, files, reactions with who reacted. */
export function renderMessage(m: EvalMessage, people: Record<string, EvalPerson>): string {
  const p = m.user === null ? undefined : people[m.user];
  const who = m.bot ? `${p?.name ?? "A bot"} [bot]` : `${p?.name ?? "Unknown person"} ${sideTag(p)}`;
  const where = m.thread === null ? "" : m.thread === m.id ? ` (thread root, ${m.replies} repl${m.replies === 1 ? "y" : "ies"})` : ` (reply in thread ${m.thread})`;
  const files = m.files.map((f) => ` [file: ${f || "unnamed"}]`).join("");
  const reacts = m.reactions.length
    ? ` {reactions: ${m.reactions.map((r) => `:${r.name}: by ${r.users.map((u) => `${people[u]?.name ?? "someone"} ${sideTag(people[u])}`).join(", ")}`).join("; ")}}`
    : "";
  return `${m.id} · ${stamp(m.ts)} · ${who}${where}: ${readable(m.text, people)}${files}${reacts}`;
}

/** What a labeller is told about one example (never its strata or the rule's answer). */
export function describeExample(ex: Example, ch: EvalChannel): string {
  const range = `${ch.messages[ex.window.from].id}–${ch.messages[Math.min(ex.window.to, ch.messages.length - 1)].id}`;
  const extra = ex.extra.filter((i) => i < ex.window.from || i > ex.window.to).map((i) => ch.messages[i].id);
  const win = `window ${range}${extra.length ? ` plus ${extra.join(", ")}` : ""}`;
  if (ex.kind === "wait") return `- **${ex.id}** (wait): target ${ex.target}; ${win}`;
  if (ex.kind === "conversation") return `- **${ex.id}** (conversation): focus ${ex.focus.join(", ")}; ${win}`;
  const cps = ex.checkpoints.map((c) => `${c.id} = ${stamp(c.ts)}`).join("; ");
  return `- **${ex.id}** (timeline): the ask raised in ${ex.seed}, about "${ex.hint}"; checkpoints ${cps}; ${win}`;
}

// ---------------------------------------------------------------- checking a label

const isIds = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string" && /^m\d+$/.test(x));
const oneOf = (v: unknown, xs: readonly string[]): boolean => typeof v === "string" && xs.includes(v);
/** Guide v4 §2.2: a ball on every open status, none on a closed or ongoing one (`waiting`, v3's, takes one too). */
export const owesFits = (status: unknown, owes: unknown): boolean => (status === "not_started" || status === "in_progress" || status === "waiting" ? owes === "us" || owes === "them" : owes === null);

/**
 * What is wrong with one label for its example, as sentences (empty: nothing).
 * `fields` narrows the check to the fields a run was asked for (a relabel of
 * one field, a candidate that answers only some).
 */
export function checkLabel(ex: Example, ch: EvalChannel, raw: unknown, fields?: readonly string[]): string[] {
  const bad: string[] = [];
  if (typeof raw !== "object" || raw === null) return ["not an object"];
  const l = raw as Record<string, unknown>;
  const want = (f: string) => fields === undefined || fields.includes(f);
  const ids = new Set(windowMessages(ex, ch).map((m) => m.id));
  const inWindow = (f: string, v: unknown) => {
    if (!isIds(v)) bad.push(`${f}: not a list of message ids`);
    else for (const id of v) if (!ids.has(id)) bad.push(`${f}: ${id} is outside the window`);
  };
  const oneId = (f: string, v: unknown, reaction: boolean) => {
    if (v === null) return;
    if (typeof v !== "string") return void bad.push(`${f}: not an id or null`);
    const r = reaction ? REACTION.exec(v) : null;
    const id = r ? r[1] : v;
    if (!ids.has(id)) bad.push(`${f}: ${v} is not a message in the window`);
  };
  if (ex.kind === "wait") {
    if (want("segment")) inWindow("segment", l.segment);
    if (want("turn")) {
      inWindow("turn", l.turn);
      if (isIds(l.turn) && !l.turn.includes(ex.target)) bad.push(`turn: must include the target ${ex.target}`);
    }
    if (want("needs_response") && typeof l.needs_response !== "boolean") bad.push("needs_response: not a boolean");
    if (want("why") && !oneOf(l.why, l.needs_response ? WHY_NEEDS : WHY_NOT)) bad.push(`why: not one of ${(l.needs_response ? WHY_NEEDS : WHY_NOT).join("|")}`);
    if (want("start")) {
      oneId("start", l.start, false);
      if (l.needs_response === true && l.start === null) bad.push("start: null though a response was needed");
    }
    if (want("answered_by")) oneId("answered_by", l.answered_by, true);
    if (want("resolution") && !oneOf(l.resolution, RESOLUTIONS)) bad.push(`resolution: not one of ${RESOLUTIONS.join("|")}`);
    if (want("kind")) {
      if (l.needs_response === true && !oneOf(l.kind, ["ack", "reply"])) bad.push("kind: ack|reply when a response is needed");
      if (l.needs_response === false && l.kind !== null) bad.push("kind: null when no response is needed");
    }
    if (want("escalation") && typeof l.escalation !== "boolean") bad.push("escalation: not a boolean");
    if (want("escalation_why") && l.escalation === true && !oneOf(l.escalation_why, ESCALATION_WHY)) bad.push(`escalation_why: one of ${ESCALATION_WHY.join("|")}`);
    if (want("need")) {
      if (l.needs_response === true && (typeof l.need !== "string" || l.need.length === 0 || l.need.length > NEED_MAX)) bad.push(`need: 1–${NEED_MAX} characters when a response is needed`);
    }
  } else if (ex.kind === "conversation") {
    if (want("asks")) {
      if (!Array.isArray(l.asks)) bad.push("asks: not a list");
      else
        l.asks.forEach((a: unknown, i: number) => {
          const k = a as Record<string, unknown>;
          if (typeof k !== "object" || k === null) return void bad.push(`asks[${i}]: not an object`);
          if (typeof k.title !== "string" || !k.title) bad.push(`asks[${i}].title: missing`);
          if (!oneOf(k.owner, ["us", "them"])) bad.push(`asks[${i}].owner: us|them`);
          if (!oneOf(k.type, ASK_TYPES)) bad.push(`asks[${i}].type: ${ASK_TYPES.join("|")}`);
          if (!oneOf(k.status, STATUSES)) bad.push(`asks[${i}].status: ${STATUSES.join("|")}`);
          if (typeof k.cluster !== "string" || !k.cluster) bad.push(`asks[${i}].cluster: missing`);
          if ("owes" in k && !owesFits(k.status, k.owes)) bad.push(`asks[${i}].owes: us|them on not_started and in_progress, null otherwise`);
          inWindow(`asks[${i}].evidence`, k.evidence);
          if (isIds(k.evidence) && k.evidence.length === 0) bad.push(`asks[${i}].evidence: empty`);
        });
    }
  } else {
    if (want("real") && typeof l.real !== "boolean") bad.push("real: not a boolean");
    if (want("owner") && !(l.owner === null || oneOf(l.owner, ["us", "them"]))) bad.push("owner: us|them|null");
    if (want("type") && !(l.type === null || oneOf(l.type, ASK_TYPES))) bad.push(`type: ${ASK_TYPES.join("|")}|null`);
    if (want("evidence")) inWindow("evidence", l.evidence);
    if (want("checkpoints")) {
      const cp = l.checkpoints as Record<string, unknown> | undefined;
      if (typeof cp !== "object" || cp === null) bad.push("checkpoints: not an object");
      else for (const c of ex.checkpoints) if (!oneOf(cp[c.id], STATUSES)) bad.push(`checkpoints.${c.id}: ${STATUSES.join("|")}`);
    }
    if (want("owes") && "owes" in l) {
      const ow = l.owes as Record<string, unknown> | undefined;
      const cp = (l.checkpoints ?? {}) as Record<string, unknown>;
      if (typeof ow !== "object" || ow === null) bad.push("owes: not an object");
      else for (const c of ex.checkpoints) if (!owesFits(cp[c.id], ow[c.id])) bad.push(`owes.${c.id}: us|them on not_started and in_progress, null otherwise`);
    }
  }
  return bad;
}

// ---------------------------------------------------------------- deterministic draws

/** A seeded generator (mulberry32): one snapshot, one set. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A copy of `xs`, shuffled by `rand` (Fisher–Yates). */
export function shuffled<T>(xs: readonly T[], rand: () => number): T[] {
  const out = xs.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const t = out[i];
    out[i] = out[j];
    out[j] = t;
  }
  return out;
}

// ---------------------------------------------------------------- numbers

export interface Rate {
  n: number;
  hits: number;
  rate: number | null;
  /** Wilson 95% interval. */
  lo: number | null;
  hi: number | null;
}

export function rate(hits: number, n: number): Rate {
  if (n === 0) return { n, hits, rate: null, lo: null, hi: null };
  const p = hits / n;
  const z = 1.96;
  const d = 1 + (z * z) / n;
  const c = (p + (z * z) / (2 * n)) / d;
  const h = (z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / d;
  return { n, hits, rate: round(p), lo: round(Math.max(0, c - h)), hi: round(Math.min(1, c + h)) };
}

const round = (x: number): number => Math.round(x * 10000) / 10000;

/** F1 of two sets (1 when both are empty). */
export function setF1(a: readonly string[], b: readonly string[]): number {
  if (a.length === 0 && b.length === 0) return 1;
  const B = new Set(b);
  const both = new Set(a.filter((x) => B.has(x))).size;
  if (both === 0) return 0;
  const p = both / new Set(a).size;
  const r = both / B.size;
  return (2 * p * r) / (p + r);
}

export interface Mean {
  n: number;
  mean: number | null;
}
const mean = (xs: number[]): Mean => ({ n: xs.length, mean: xs.length ? round(xs.reduce((s, x) => s + x, 0) / xs.length) : null });

const median = (xs: number[]): number | null => {
  if (xs.length === 0) return null;
  const s = xs.slice().sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

export interface Binary {
  n: number;
  accuracy: Rate;
  /** Of the candidate's positives, how many the reference has. */
  precision: Rate;
  /** Of the reference's positives, how many the candidate has. */
  recall: Rate;
  kappa: number | null;
}

/** Agreement on a yes/no field, the reference first. */
export function binary(pairs: ReadonlyArray<[boolean, boolean]>): Binary {
  let tp = 0,
    fp = 0,
    fn = 0,
    tn = 0;
  for (const [r, c] of pairs) {
    if (r && c) tp++;
    else if (!r && c) fp++;
    else if (r && !c) fn++;
    else tn++;
  }
  const n = pairs.length;
  const po = n ? (tp + tn) / n : 0;
  const pe = n ? ((tp + fp) * (tp + fn) + (fn + tn) * (fp + tn)) / (n * n) : 0;
  return { n, accuracy: rate(tp + tn, n), precision: rate(tp, tp + fp), recall: rate(tp, tp + fn), kappa: n && pe < 1 ? round((po - pe) / (1 - pe)) : null };
}

/** Agreement on a categorical field, with the confusion matrix (reference → candidate → count). */
export function categorical(pairs: ReadonlyArray<[string, string]>): { accuracy: Rate; confusion: Record<string, Record<string, number>> } {
  const confusion: Record<string, Record<string, number>> = {};
  let hits = 0;
  for (const [r, c] of pairs) {
    if (r === c) hits++;
    (confusion[r] ??= {})[c] = (confusion[r]?.[c] ?? 0) + 1;
  }
  return { accuracy: rate(hits, pairs.length), confusion };
}

// ---------------------------------------------------------------- matching asks

/** Pairs each candidate ask with at most one reference ask, by most shared evidence (none shared: unmatched). */
export function matchAsks<A extends { evidence: string[] }, B extends { evidence: string[] }>(ref: readonly A[], cand: readonly B[]): Array<[number, number]> {
  const scored: Array<[number, number, number]> = [];
  ref.forEach((r, i) =>
    cand.forEach((c, j) => {
      const s = setF1(r.evidence, c.evidence);
      if (s > 0) scored.push([s, i, j]);
    }),
  );
  scored.sort((a, b) => b[0] - a[0] || a[1] - b[1] || a[2] - b[2]);
  const usedR = new Set<number>();
  const usedC = new Set<number>();
  const out: Array<[number, number]> = [];
  for (const [, i, j] of scored) {
    if (usedR.has(i) || usedC.has(j)) continue;
    usedR.add(i);
    usedC.add(j);
    out.push([i, j]);
  }
  return out.sort((a, b) => a[0] - b[0]);
}

const isWork = (t: string | null | undefined): boolean => t === "task" || t === "standing_instruction";

// ---------------------------------------------------------------- comparing two runs

/** A message's ts, in seconds, from its id. */
type TsOf = (exampleId: string, id: string) => number | null;

export interface Comparison {
  counts: Record<ExampleKind, number>;
  waits: {
    segment: Mean;
    turn: Mean;
    /** Of the reference's utterances of two or more messages, the share the candidate kept as one (C1's fragment merge). */
    fragmentMerge: Rate;
    needsResponse: Binary;
    start: Rate;
    answered: Binary;
    answeredBy: Rate;
    /** Both answered by a message, the reference needing a response: |candidate − reference| first-response time, each from its own start. */
    firstResponse: { n: number; medianMinutes: number | null; within15m: Rate };
    resolution: { accuracy: Rate; confusion: Record<string, Record<string, number>> };
    kind: { accuracy: Rate; confusion: Record<string, Record<string, number>> };
    escalation: Binary;
  };
  conversations: {
    hasAsk: Binary;
    /** Work asks (task, standing instruction) matched by shared evidence. */
    asks: { precision: Rate; recall: Rate; f1: number | null };
    owner: Rate;
    type: Rate;
    status: Rate;
    /** Per reference cluster of a brand, the candidate's distinct clusters matched to it beyond the first, over matched work asks. */
    duplicates: Rate;
    /** The candidate's asks that are scheduling by the reference (not work, C9). */
    schedulingAsWork: Rate;
  };
  timelines: {
    real: Binary;
    owner: Rate;
    evidence: Mean;
    checkpoints: Rate;
    /** A candidate's writes that are evidence by the reference (C12). */
    updatePrecision: Rate;
    updatesPerItem: Mean;
  };
}

/**
 * Every metric that compares a candidate to a reference, over the examples
 * both answered. The reference is gold, or another labeller (then these are
 * the agreement numbers, D3). `brandOf` maps an example to its brand, for
 * duplicates across one brand's conversations.
 */
export function compare(
  examples: ReadonlyMap<string, Example>,
  ref: ReadonlyMap<string, Label>,
  cand: ReadonlyMap<string, Label>,
  tsOf: TsOf,
  brandOf: (exampleId: string) => string,
): Comparison {
  const counts: Record<ExampleKind, number> = { wait: 0, conversation: 0, timeline: 0 };
  const seg: number[] = [],
    turn: number[] = [];
  let fragN = 0,
    fragHit = 0;
  const needs: Array<[boolean, boolean]> = [];
  let startN = 0,
    startHit = 0;
  const answered: Array<[boolean, boolean]> = [];
  let byN = 0,
    byHit = 0;
  const frt: number[] = [];
  const resolution: Array<[string, string]> = [];
  const kind: Array<[string, string]> = [];
  const esc: Array<[boolean, boolean]> = [];

  const hasAsk: Array<[boolean, boolean]> = [];
  let candWork = 0,
    refWork = 0,
    matchedWork = 0,
    ownerN = 0,
    ownerHit = 0,
    typeN = 0,
    typeHit = 0,
    statusN = 0,
    statusHit = 0,
    sched = 0,
    candAll = 0;
  /** Per brand and reference cluster, the candidate's clusters matched to it. */
  const clusterHits = new Map<string, Set<string>>();

  const real: Array<[boolean, boolean]> = [];
  let tOwnerN = 0,
    tOwnerHit = 0,
    cpN = 0,
    cpHit = 0,
    upN = 0,
    upHit = 0;
  const evid: number[] = [];
  const ups: number[] = [];

  const ids = Array.from(examples.keys()).sort();
  for (const id of ids) {
    const ex = examples.get(id) as Example;
    const r = ref.get(id);
    const c = cand.get(id);
    if (r === undefined || c === undefined) continue;
    counts[ex.kind]++;
    if (ex.kind === "wait") {
      const R = r as WaitLabel,
        C = c as WaitLabel;
      if (R.segment && C.segment) seg.push(setF1(R.segment, C.segment));
      if (R.turn && C.turn) {
        turn.push(setF1(R.turn, C.turn));
        if (R.turn.length >= 2) {
          fragN++;
          if (R.turn.every((m) => C.turn.includes(m))) fragHit++;
        }
      }
      if (typeof R.needs_response === "boolean" && typeof C.needs_response === "boolean") needs.push([R.needs_response, C.needs_response]);
      if (R.needs_response && C.needs_response && R.start !== undefined && C.start !== undefined) {
        startN++;
        if (R.start === C.start) startHit++;
      }
      if (R.answered_by !== undefined && C.answered_by !== undefined) {
        byN++;
        const rBy = stripTone(R.answered_by),
          cBy = stripTone(C.answered_by);
        if (rBy === cBy) byHit++;
        if (R.needs_response) {
          answered.push([rBy !== null, cBy !== null]);
          if (rBy !== null && cBy !== null) {
            // Reactions carry no time (E1): only two messages are timed.
            if (!REACTION.test(rBy) && !REACTION.test(cBy)) {
              const a = tsOf(id, rBy);
              const b = tsOf(id, cBy);
              // The response time is from each side's own start (C3: the wait's
              // start is part of the answer), else both from the target.
              const ra = R.start ? tsOf(id, R.start) : tsOf(id, ex.target);
              const ca = C.start ? tsOf(id, C.start) : tsOf(id, ex.target);
              if (a !== null && b !== null && ra !== null && ca !== null) frt.push(Math.abs(a - ra - (b - ca)) / 60);
            }
          }
        }
      }
      if (R.resolution && C.resolution) resolution.push([R.resolution, C.resolution]);
      if (R.needs_response && C.needs_response && R.kind && C.kind) kind.push([R.kind, C.kind]);
      if (typeof R.escalation === "boolean" && typeof C.escalation === "boolean") esc.push([R.escalation, C.escalation]);
    } else if (ex.kind === "conversation") {
      const R = (r as ConversationLabel).asks ?? [],
        C = (c as ConversationLabel).asks ?? [];
      const rw = R.filter((a) => isWork(a.type)),
        cw = C.filter((a) => isWork(a.type));
      hasAsk.push([rw.length > 0, cw.length > 0]);
      refWork += rw.length;
      candWork += cw.length;
      candAll += C.length;
      const pairs = matchAsks(R, C);
      for (const [i, j] of pairs) {
        const a = R[i],
          b = C[j];
        if (isWork(a.type) && isWork(b.type)) {
          matchedWork++;
          const key = `${brandOf(id)}\u0000${a.cluster}`;
          const seen = clusterHits.get(key) ?? new Set<string>();
          seen.add(b.cluster);
          clusterHits.set(key, seen);
        }
        if (a.type === "scheduling" && isWork(b.type)) sched++;
        ownerN++;
        if (a.owner === b.owner) ownerHit++;
        typeN++;
        if (a.type === b.type) typeHit++;
        statusN++;
        if (a.status === b.status) statusHit++;
      }
    } else {
      const R = r as TimelineLabel,
        C = c as TimelineLabel;
      if (typeof R.real === "boolean" && typeof C.real === "boolean") real.push([R.real, C.real]);
      if (!R.real) continue;
      if (R.owner && C.owner !== undefined) {
        tOwnerN++;
        if (R.owner === C.owner) tOwnerHit++;
      }
      if (R.evidence && C.evidence) evid.push(setF1(R.evidence, C.evidence));
      if (R.checkpoints && C.checkpoints)
        for (const cp of ex.checkpoints) {
          if (R.checkpoints[cp.id] === undefined || C.checkpoints[cp.id] === undefined) continue;
          cpN++;
          if (R.checkpoints[cp.id] === C.checkpoints[cp.id]) cpHit++;
        }
      if (C.updates) {
        ups.push(C.updates.length);
        upN += C.updates.length;
        upHit += C.updates.filter((u) => R.evidence.includes(u)).length;
      }
    }
  }
  let dup = 0;
  // One piece of work the candidate holds as two items is a duplicate; the
  // same item found again in another conversation is not (E2: prod's items).
  clusterHits.forEach((s) => (dup += s.size - 1));
  const p = rate(matchedWork, candWork),
    rc = rate(matchedWork, refWork);
  return {
    counts,
    waits: {
      segment: mean(seg),
      turn: mean(turn),
      fragmentMerge: rate(fragHit, fragN),
      needsResponse: binary(needs),
      start: rate(startHit, startN),
      answered: binary(answered),
      answeredBy: rate(byHit, byN),
      firstResponse: { n: frt.length, medianMinutes: median(frt), within15m: rate(frt.filter((x) => x <= 15).length, frt.length) },
      resolution: categorical(resolution),
      kind: categorical(kind),
      escalation: binary(esc),
    },
    conversations: {
      hasAsk: binary(hasAsk),
      asks: { precision: p, recall: rc, f1: p.rate !== null && rc.rate !== null && p.rate + rc.rate > 0 ? round((2 * p.rate * rc.rate) / (p.rate + rc.rate)) : null },
      owner: rate(ownerHit, ownerN),
      type: rate(typeHit, typeN),
      status: rate(statusHit, statusN),
      duplicates: rate(dup, matchedWork),
      schedulingAsWork: rate(sched, candAll),
    },
    timelines: {
      real: binary(real),
      owner: rate(tOwnerHit, tOwnerN),
      evidence: mean(evid),
      checkpoints: rate(cpHit, cpN),
      updatePrecision: rate(upHit, upN),
      updatesPerItem: mean(ups),
    },
  };
}

/**
 * The fields D3's 85% bar applies to, read off a comparison of two labellers:
 * each field's agreement as one number in [0, 1]. Free text (need lines, ask
 * titles, notes) and the fine reasons (`why`, `escalation_why`, `resolution`)
 * are reported, not gated: E2–E6 score none of them against gold.
 */
export function gatedAgreement(c: Comparison): Record<string, number | null> {
  return {
    "wait.segment": c.waits.segment.mean,
    "wait.turn": c.waits.turn.mean,
    "wait.needs_response": c.waits.needsResponse.accuracy.rate,
    "wait.start": c.waits.start.rate,
    "wait.answered_by": c.waits.answeredBy.rate,
    "wait.kind": c.waits.kind.accuracy.rate,
    "wait.escalation": c.waits.escalation.accuracy.rate,
    "conversation.has_ask": c.conversations.hasAsk.accuracy.rate,
    "conversation.asks": c.conversations.asks.f1,
    "conversation.owner": c.conversations.owner.rate,
    "conversation.type": c.conversations.type.rate,
    "conversation.status": c.conversations.status.rate,
    "timeline.real": c.timelines.real.accuracy.rate,
    "timeline.owner": c.timelines.owner.rate,
    "timeline.evidence": c.timelines.evidence.mean,
    "timeline.checkpoints": c.timelines.checkpoints.rate,
  };
}

export const AGREEMENT_BAR = 0.85;

// ---------------------------------------------------------------- the judge's scores (C5, C6, C8)

export interface JudgeScore {
  id: string;
  emoji?: number;
  quick?: number;
  would_send?: boolean;
  need_faithful?: boolean;
}

export function judged(scores: readonly JudgeScore[]): { emoji: Mean; quick: Mean; wouldSend: Rate; needFaithful: Rate } {
  const nums = (f: "emoji" | "quick") => scores.map((s) => s[f]).filter((x): x is number => typeof x === "number");
  const bools = (f: "would_send" | "need_faithful") => scores.map((s) => s[f]).filter((x): x is boolean => typeof x === "boolean");
  const ws = bools("would_send"),
    nf = bools("need_faithful");
  return { emoji: mean(nums("emoji")), quick: mean(nums("quick")), wouldSend: rate(ws.filter(Boolean).length, ws.length), needFaithful: rate(nf.filter(Boolean).length, nf.length) };
}

// ---------------------------------------------------------------- E12: gold folds a client's review into our ask

/**
 * One fold (guide v3, E12): the label's `them` ask at index `them`
 * (a client's review, approval or pick of our work) joins our `us` ask at
 * index `into`, whose status becomes `status` (the merged ask's, re-judged).
 */
export interface Fold {
  them: number;
  into: number;
  status: Status;
}

/**
 * A conversation label with its folds applied: each folded `them` ask leaves
 * the list, and the `us` ask it joins takes its evidence (our first message
 * stays first; the rest in message order) and the fold's status. Everything
 * else, order included, is untouched. Refuses a fold that isn't them → us,
 * folds one ask twice, or gives one `us` ask two statuses.
 */
export function foldReviews(l: ConversationLabel, folds: readonly Fold[]): ConversationLabel {
  const gone = new Set<number>();
  const into = new Map<number, { evidence: Set<string>; status: Status }>();
  for (const f of folds) {
    const t = l.asks[f.them],
      u = l.asks[f.into];
    if (!t || !u) throw new Error(`${l.id}: fold ${f.them} → ${f.into} names no ask`);
    if (t.owner !== "them" || u.owner !== "us") throw new Error(`${l.id}: fold ${f.them} → ${f.into} is not them → us`);
    if (gone.has(f.them)) throw new Error(`${l.id}: ask ${f.them} folded twice`);
    if (!(STATUSES as readonly string[]).includes(f.status)) throw new Error(`${l.id}: fold status ${f.status}`);
    gone.add(f.them);
    const cur = into.get(f.into);
    if (cur && cur.status !== f.status) throw new Error(`${l.id}: ask ${f.into} given two statuses`);
    const ev = cur?.evidence ?? new Set<string>();
    for (const m of t.evidence) ev.add(m);
    into.set(f.into, { evidence: ev, status: f.status });
  }
  const asks: Ask[] = [];
  l.asks.forEach((a, i) => {
    if (gone.has(i)) return;
    const f = into.get(i);
    if (!f) return void asks.push(a);
    const [first, ...rest] = a.evidence;
    const tail = Array.from(new Set([...rest, ...f.evidence])).filter((m) => m !== first);
    tail.sort((x, y) => idNum(x) - idNum(y));
    asks.push({ ...a, evidence: first === undefined ? tail : [first, ...tail], status: f.status });
  });
  return { ...l, asks };
}

/** Where one `them` ask goes under a set of folds: the `us` index it joins, or "keep". */
export const foldOf = (folds: readonly Fold[], them: number): number | "keep" => folds.find((f) => f.them === them)?.into ?? "keep";

/**
 * Two labellers' folds on the same examples (E1's cross-check, D3): per
 * `them` ask, whether both send it to the same place (an `us` ask or
 * "keep"); per example, whether every decision matches; and, where both
 * fold an ask into the same `us` ask, whether they give it the same status.
 */
export function foldAgreement(thems: ReadonlyMap<string, readonly number[]>, a: ReadonlyMap<string, readonly Fold[]>, b: ReadonlyMap<string, readonly Fold[]>) {
  let n = 0,
    hits = 0,
    exN = 0,
    exHits = 0,
    stN = 0,
    stHits = 0;
  const folded: Array<[boolean, boolean]> = [];
  for (const [id, ts] of thems) {
    const fa = a.get(id),
      fb = b.get(id);
    if (!fa || !fb) continue;
    exN++;
    let all = true;
    for (const t of ts) {
      const x = foldOf(fa, t),
        y = foldOf(fb, t);
      n++;
      folded.push([x !== "keep", y !== "keep"]);
      if (x === y) hits++;
      else all = false;
      if (x !== "keep" && x === y) {
        stN++;
        if (fa.find((f) => f.them === t)?.status === fb.find((f) => f.them === t)?.status) stHits++;
      }
    }
    if (all) exHits++;
  }
  return { decisions: rate(hits, n), examples: rate(exHits, exN), status: rate(stHits, stN), folded: binary(folded) };
}

// ---------------------------------------------------------------- L1: every ask is ours, and who has the ball

/*
 * Guide v4. Gold v3 is gold v2 relabelled: every ask is `us`, each open ask and
 * checkpoint says who owes the next move (`owes`), and there is no `waiting`.
 */

/** Open (to do, in progress), closed (done, dropped), or ongoing; null for a status that isn't one (`unread`: no item yet). */
export type Openness = "open" | "closed" | "ongoing";

/**
 * A label's status and ball under guide v4, whatever version wrote it. A v4
 * label (gold v3, an items v4 answer) carries `owes` and is read as it is.
 * Anything older is read the way prod's `items.v3` behaves:
 * `waiting` is in progress with the client owing, and the ball of any other
 * open status is the ask's owner, written once and never moving.
 */
export function ballOf(status: Status | string, owner: "us" | "them" | null, owes?: Owes): { status: Status | string; owes: Owes } {
  if (status === "waiting") return { status: "in_progress", owes: owes === undefined ? "them" : owes };
  const open = status === "not_started" || status === "in_progress";
  if (owes !== undefined) return { status, owes: open ? owes : null };
  return { status, owes: open ? owner : null };
}

export function openness(status: Status | string): Openness | null {
  if (status === "not_started" || status === "in_progress" || status === "waiting") return "open";
  if (status === "done" || status === "dropped") return "closed";
  return status === "ongoing" ? "ongoing" : null;
}

/** The status merged to three: to do; in progress with ongoing (and v3's waiting); done with dropped. */
export function status3(status: Status | string): "todo" | "in_progress" | "done" | null {
  if (status === "not_started") return "todo";
  if (status === "in_progress" || status === "ongoing" || status === "waiting") return "in_progress";
  return status === "done" || status === "dropped" ? "done" : null;
}

/** One gold ask's fate under guide v4: kept (a `them` ask kept as ours takes a YWS title), folded into another kept ask, or dropped as not an ask. */
export type AskDecision = { kind: "keep"; title?: string; status: Status; owes: Owes } | { kind: "fold"; into: number } | { kind: "drop" };

/** An ask guide v4 needs that the label lacks (case 6's shape), with its evidence. */
export interface AddedAsk {
  title: string;
  type: AskType;
  evidence: string[];
  status: Status;
  owes: Owes;
}

/** A labeller's answer for one conversation example: a decision per gold ask, by index, and any asks to add. */
export interface Relabel {
  asks: AskDecision[];
  added: AddedAsk[];
}

const isV4 = (s: string): s is Status => (V4_STATUSES as readonly string[]).includes(s);

/**
 * A conversation label rewritten under guide v4. Each kept ask becomes ours
 * with its decision's status and ball (and title, when given); a folded ask
 * leaves the list and its evidence joins the ask it folds into (that ask's
 * first message stays first, the rest in message order); a dropped ask goes;
 * added asks follow, ours, each its own new cluster. Order is otherwise
 * kept. Refuses: a decision count that isn't the label's ask count, folding
 * or dropping one of our own asks (two `us` asks are never merged here, as
 * E12), a fold into an ask that isn't kept, a `waiting` status, or a ball
 * that doesn't fit its status (`owesFits`).
 */
export function relabel(l: ConversationLabel, r: Relabel): ConversationLabel {
  if (r.asks.length !== l.asks.length) throw new Error(`${l.id}: ${r.asks.length} decisions for ${l.asks.length} asks`);
  const check = (where: string, status: string, owes: Owes) => {
    if (!isV4(status)) throw new Error(`${l.id}: ${where} status ${status} is not a v4 status`);
    if (!owesFits(status, owes)) throw new Error(`${l.id}: ${where} owes ${owes} doesn't fit ${status}`);
  };
  const extra = new Map<number, Set<string>>();
  r.asks.forEach((d, i) => {
    const a = l.asks[i];
    if (d.kind !== "keep" && a.owner === "us") throw new Error(`${l.id}: a${i + 1} is ours and can only be kept`);
    if (d.kind === "keep") return check(`a${i + 1}`, d.status, d.owes);
    if (d.kind === "fold") {
      const t = r.asks[d.into];
      if (d.into === i || !t || t.kind !== "keep") throw new Error(`${l.id}: a${i + 1} folds into a${d.into + 1}, which isn't kept`);
      const ev = extra.get(d.into) ?? new Set<string>();
      for (const m of a.evidence) ev.add(m);
      extra.set(d.into, ev);
    }
  });
  const asks: Ask[] = [];
  r.asks.forEach((d, i) => {
    if (d.kind !== "keep") return;
    const a = l.asks[i];
    const [first, ...rest] = a.evidence;
    const tail = Array.from(new Set([...rest, ...(extra.get(i) ?? [])])).filter((m) => m !== first);
    tail.sort((x, y) => idNum(x) - idNum(y));
    asks.push({ ...a, title: d.title?.trim() || a.title, owner: "us", evidence: first === undefined ? tail : [first, ...tail], status: d.status, owes: d.owes });
  });
  r.added.forEach((a, k) => {
    check(`added ${k + 1}`, a.status, a.owes);
    if (!a.evidence.length) throw new Error(`${l.id}: added ${k + 1} has no evidence`);
    asks.push({ title: a.title, owner: "us", type: a.type, evidence: a.evidence, status: a.status, cluster: `${l.id}-add${k + 1}`, owes: a.owes });
  });
  return { ...l, asks };
}

/** A labeller's answer for one timeline: whether it is an ask under v4, and each checkpoint's status and ball. */
export interface TimelineRelabel {
  real: boolean;
  checkpoints: Record<string, { status: Status; owes: Owes }>;
}

/** A timeline label rewritten under guide v4: ours, with the answer's `real`, statuses and balls; evidence and type as they were. Refuses a missing checkpoint, `waiting`, or a ball that doesn't fit. */
export function relabelTimeline(l: TimelineLabel, cps: readonly string[], r: TimelineRelabel): TimelineLabel {
  const checkpoints: Record<string, Status> = {},
    owes: Record<string, Owes> = {};
  for (const cp of cps) {
    const c = r.checkpoints[cp];
    if (!c) throw new Error(`${l.id}: no answer for ${cp}`);
    if (!isV4(c.status)) throw new Error(`${l.id}: ${cp} status ${c.status} is not a v4 status`);
    if (!owesFits(c.status, c.owes)) throw new Error(`${l.id}: ${cp} owes ${c.owes} doesn't fit ${c.status}`);
    checkpoints[cp] = c.status;
    owes[cp] = c.owes;
  }
  return { ...l, real: r.real, owner: r.real || l.owner !== null ? "us" : null, checkpoints, owes };
}

/**
 * Per example, the numerators and denominators of the ball metrics, a
 * candidate against a reference, both read through `ballOf`:
 *
 *   - askOpen / askStatus5 / askStatus3: over asks matched by shared
 *     evidence (`matchAsks`), as `compare`'s status;
 *   - askOwes: over those the reference calls open;
 *   - cpOpen / cpStatus5 / cpStatus3 / cpOwes: the same per checkpoint of a
 *     timeline the reference calls real;
 *   - perAsk: per reference work ask, the candidate's asks sharing evidence
 *     with it (items per gold ask: 1 is one item per piece of work).
 *
 * `pairs` are the matched (reference, candidate) statuses after the read,
 * asks then checkpoints, for confusions (`ongoing` against in progress,
 * `dropped` against done).
 */
export interface BallParts {
  askOpen: [number, number];
  askOwes: [number, number];
  askStatus5: [number, number];
  askStatus3: [number, number];
  cpOpen: [number, number];
  cpOwes: [number, number];
  cpStatus5: [number, number];
  cpStatus3: [number, number];
  perAsk: [number, number];
  pairs: Array<[string, string]>;
}

export function ballParts(ex: Example, ref: Label | undefined, cand: Label | undefined): BallParts {
  const z = (): [number, number] => [0, 0];
  const out: BallParts = { askOpen: z(), askOwes: z(), askStatus5: z(), askStatus3: z(), cpOpen: z(), cpOwes: z(), cpStatus5: z(), cpStatus3: z(), perAsk: z(), pairs: [] };
  if (!ref || !cand) return out;
  const tally = (prefix: "ask" | "cp", r: { status: Status | string; owes: Owes }, c: { status: Status | string; owes: Owes }) => {
    const k = (n: string) => `${prefix}${n}` as keyof Omit<BallParts, "pairs">;
    const add = (key: keyof Omit<BallParts, "pairs">, hit: boolean) => ((out[key][0] += hit ? 1 : 0), (out[key][1] += 1));
    const ro = openness(r.status);
    add(k("Open"), ro !== null && ro === openness(c.status));
    add(k("Status5"), r.status === c.status);
    add(k("Status3"), status3(r.status) !== null && status3(r.status) === status3(c.status));
    if (ro === "open") add(k("Owes"), r.owes === c.owes && openness(c.status) === "open");
    out.pairs.push([String(r.status), String(c.status)]);
  };
  if (ex.kind === "conversation") {
    const R = (ref as ConversationLabel).asks ?? [],
      C = (cand as ConversationLabel).asks ?? [];
    for (const [i, j] of matchAsks(R, C)) tally("ask", ballOf(R[i].status, R[i].owner, R[i].owes), ballOf(C[j].status, C[j].owner, C[j].owes));
    for (const a of R)
      if (isWork(a.type)) {
        out.perAsk[0] += C.filter((c) => setF1(a.evidence, c.evidence) > 0).length;
        out.perAsk[1] += 1;
      }
  } else if (ex.kind === "timeline") {
    const R = ref as TimelineLabel,
      C = cand as TimelineLabel;
    if (!R.real) return out;
    for (const cp of ex.checkpoints) {
      const rs = R.checkpoints?.[cp.id],
        cs = C.checkpoints?.[cp.id];
      if (rs === undefined || cs === undefined) continue;
      tally("cp", ballOf(rs, R.owner, R.owes?.[cp.id]), ballOf(cs, C.owner, C.owes?.[cp.id]));
    }
  }
  return out;
}

/** Ball parts summed over examples. */
export function sumBall(ps: readonly BallParts[]): BallParts {
  const out = ballParts({ kind: "wait" } as Example, undefined, undefined);
  for (const p of ps) {
    for (const k of Object.keys(out) as Array<keyof BallParts>) if (k !== "pairs") ((out[k][0] += p[k][0]), (out[k][1] += p[k][1]));
    out.pairs.push(...p.pairs);
  }
  return out;
}

/**
 * Whether two readings tell `kind` (ongoing or dropped) from its parent (in
 * progress, done), over the pairs where both readings are one of the two:
 * the agreement, and how many pairs either reading called `kind`.
 */
export function kindAgreement(pairs: ReadonlyArray<[string, string]>, kind: "ongoing" | "dropped"): { n: number; either: number; both: number; agreement: Rate; kappa: number | null } {
  const parent = kind === "ongoing" ? "in_progress" : "done";
  const inPair = pairs.filter(([a, b]) => (a === kind || a === parent) && (b === kind || b === parent));
  const bin = binary(inPair.map(([a, b]) => [a === kind, b === kind]));
  return { n: inPair.length, either: inPair.filter(([a, b]) => a === kind || b === kind).length, both: inPair.filter(([a, b]) => a === kind && b === kind).length, agreement: bin.accuracy, kappa: bin.kappa };
}

/**
 * B1: an ask is on the page when it is work (a task or a
 * standing instruction), or scheduling still open. Scheduling settled in the
 * same exchange stays off the tabs, as P6 has it; an unanswered "could we do
 * Thursday?" is work on the page with its ball.
 */
export const onPage = (a: { type: string; status: Status | string }): boolean => isWork(a.type) || (a.type === "scheduling" && openness(a.status) === "open");

/**
 * Ask precision and recall over the asks on the page (`onPage`, each side by
 * its own status), matched by shared evidence as `compare`'s: a pair counts
 * when both asks are on the page. Ask F1 over work alone is `compare`'s.
 */
export function pageAskParts(ref: readonly Ask[], cand: readonly Ask[]): { precision: [number, number]; recall: [number, number] } {
  const matched = matchAsks(ref, cand).filter(([i, j]) => onPage(ref[i]) && onPage(cand[j])).length;
  return { precision: [matched, cand.filter(onPage).length], recall: [matched, ref.filter(onPage).length] };
}

/**
 * How a candidate reads `kind` (ongoing or dropped) against a reference,
 * over matched (reference, candidate) status pairs: recall is the reference's
 * `kind` the candidate also calls `kind`, precision the candidate's `kind`
 * the reference also calls `kind`, and what the candidate said instead, and
 * what the reference said where the candidate was wrong. I1 keeps a kind when
 * both are at least 0.8 over its two runs.
 */
export function kindPR(
  pairs: ReadonlyArray<[string, string]>,
  kind: "ongoing" | "dropped",
): { recall: Rate; precision: Rate; candidateSaid: Record<string, number>; referenceSaid: Record<string, number> } {
  const tally = (xs: string[]) => xs.reduce<Record<string, number>>((o, s) => ((o[s] = (o[s] ?? 0) + 1), o), {});
  const ref = pairs.filter(([a]) => a === kind),
    cand = pairs.filter(([, b]) => b === kind);
  return {
    recall: rate(ref.filter(([, b]) => b === kind).length, ref.length),
    precision: rate(cand.filter(([a]) => a === kind).length, cand.length),
    candidateSaid: tally(ref.map(([, b]) => b)),
    referenceSaid: tally(cand.map(([a]) => a)),
  };
}

/**
 * B1's read-rule variant (no new calls): a v3-era candidate's `them` ask that
 * hasn't started is something we asked the client for, which guide v4 calls
 * in progress with the client owing (the start rule). Every other ask, a
 * label that already carries `owes`, and gold are left as they are. A
 * timeline's checkpoints move the same way when its owner is `them`.
 */
export function startedRead(l: Label): Label {
  if ("asks" in l && Array.isArray((l as ConversationLabel).asks)) {
    const c = l as ConversationLabel;
    return { ...c, asks: c.asks.map((a) => (a.owner === "them" && a.status === "not_started" && a.owes === undefined ? { ...a, status: "in_progress" as Status } : a)) };
  }
  const t = l as TimelineLabel;
  if (!t.checkpoints || t.owner !== "them" || t.owes !== undefined) return l;
  return { ...t, checkpoints: Object.fromEntries(Object.entries(t.checkpoints).map(([k, s]) => [k, s === "not_started" ? ("in_progress" as Status) : s])) };
}
